// DESCRIPTION: Horizontal list item component for list layout displaying
// event image, title, date, location, price, description, and action buttons.

import { __ } from '@wordpress/i18n';
import PriceBadge from './PriceBadge';
import { getEventStyleVars } from '../utils/eventStyling';

const EventListItem = ( { event, onViewEvent } ) => {
	return (
		<li
			className="nb-flex nb-flex-col nb-gap-10 nb-py-12 first:nb-pt-0 last:nb-pb-0 sm:nb-flex-row"
			style={ getEventStyleVars( event ) }
		>
			{ /* Image */ }
			<div className="nb-aspect-[4/5] nb-w-52 nb-flex-none nb-rounded-2xl nb-overflow-hidden nb-outline nb-outline-1 nb--outline-offset-1 nb-outline-black/5">
				{ event.image_url ? (
					<img
						src={ event.image_url }
						alt={ event.title }
						className="nb-w-full nb-h-full nb-object-cover"
						loading="lazy"
					/>
				) : (
					<div className="nb-relative nb-flex nb-flex-col nb-items-center nb-justify-center nb-h-full nb-bg-gradient-to-br nb-from-slate-50 nb-to-slate-100 nb-p-6">
						<div className="nb-w-12 nb-h-12 nb-bg-slate-600 nb-rounded-full nb-flex nb-items-center nb-justify-center nb-mb-4">
							<svg
								className="nb-w-6 nb-h-6 nb-text-white"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={ 2 }
									d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
								/>
							</svg>
						</div>

						{ event.event_type !== 'one-on-one' &&
							event.event_type !== 'group' && (
								<time
									dateTime={ event.date }
									className="nb-text-sm nb-font-medium nb-text-slate-600 nb-text-center nb-mb-2"
								>
									{ event.date && event.time
										? `${ event.date } at ${ event.time }`
										: event.date || event.time }
								</time>
							) }

						<h4 className="nb-text-sm nb-font-semibold nb-text-slate-800 nb-text-center nb-leading-tight">
							{ event.title }
						</h4>

						{ /* Decorative Elements */ }
						<div className="nb-absolute nb-top-2 nb-right-2 nb-w-2 nb-h-2 nb-bg-slate-300 nb-rounded-full"></div>
						<div className="nb-absolute nb-bottom-2 nb-left-2 nb-w-1 nb-h-1 nb-bg-slate-200 nb-rounded-full"></div>
					</div>
				) }
			</div>

			{ /* Content */ }
			<div className="nb-max-w-xl nb-flex-auto">
				<div className="nb-flex nb-items-start nb-gap-3 nb-mb-1">
					<h3 className="nb-text-lg nb-leading-8 nb-font-semibold nb-tracking-tight nb-text-gray-900">
						{ event.title }
					</h3>
					<PriceBadge
						price={ event.price }
						currencySymbol={ event.currency_symbol }
					/>
				</div>

				{ event.event_type !== 'one-on-one' &&
					event.event_type !== 'group' && (
						<p className="nb-text-base nb-leading-7 nb-text-gray-600">
							{ event.date && event.time
								? `${ event.date } at ${ event.time }`
								: event.date || event.time }
						</p>
					) }

				{ event.location && (
					<p className="nb-text-base nb-leading-7 nb-text-gray-600">
						{ event.location }
					</p>
				) }

				{ event.description && (
					<div
						className="nb-mt-6 nb-text-base nb-leading-7 nb-text-gray-600 nb-line-clamp-3"
						dangerouslySetInnerHTML={ {
							__html: event.description,
						} }
					/>
				) }

				<div className="nb-mt-6">
					<button
						className="nb-event-list-item__cta nb-transition-all nb-duration-200"
						onClick={ () => onViewEvent( event.id, event.slug ) }
						data-event-id={ event.id }
					>
						<svg
							className="nb-w-4 nb-h-4"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={ 2 }
								d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
							/>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={ 2 }
								d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
							/>
						</svg>
						{ __( 'View Event', 'nemtly-booking' ) }
					</button>
				</div>
			</div>
		</li>
	);
};

export default EventListItem;
